/* Selvaro — (i) info tooltip custom element.
   Hover (desktop) is handled by CSS; this script adds tap/click toggling,
   keyboard support, outside-click closing, Escape dismissal and viewport-edge
   repositioning. */
if (!customElements.get('selvaro-tooltip')) {
  customElements.define(
    'selvaro-tooltip',
    class SelvaroTooltip extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('.selvaro-tip__button');
        this.bubble = this.querySelector('.selvaro-tip__bubble');

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);
      }

      connectedCallback() {
        if (!this.button || !this.bubble) return;

        this.button.addEventListener('click', this.onButtonClick);
        this.button.addEventListener('mouseenter', this.onPointerEnter);
        this.button.addEventListener('mouseleave', this.onPointerLeave);
        this.button.addEventListener('focus', this.onPointerEnter);
        /* pointerdown is the primary outside-close channel: on iOS Safari a tap
           on a non-interactive element never produces a document click event,
           and a tap on a button does not move focus, so neither click nor
           focusout would fire there. */
        document.addEventListener('pointerdown', this.onDocumentClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        this.addEventListener('focusout', this.onFocusOut);
        window.addEventListener('resize', this.onResize, { passive: true });

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener('click', this.onButtonClick);
          this.button.removeEventListener('mouseenter', this.onPointerEnter);
          this.button.removeEventListener('mouseleave', this.onPointerLeave);
          this.button.removeEventListener('focus', this.onPointerEnter);
        }
        document.removeEventListener('pointerdown', this.onDocumentClick);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('focusout', this.onFocusOut);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      get isOpen() {
        return this.classList.contains('is-open');
      }

      /* The bubble has a layout box only while it is on screen (display:none
         otherwise), so this doubles as "is the tooltip currently visible". */
      get isVisible() {
        return !!this.bubble && this.bubble.getBoundingClientRect().width > 0;
      }

      onButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      onPointerEnter(event) {
        this.classList.remove('is-dismissed');

        if (event && event.type === 'focus') {
          var keyboard = false;
          try {
            keyboard = !!this.button && this.button.matches(':focus-visible');
          } catch (error) {
            keyboard = false;
          }
          if (keyboard) {
            this.open();
            return;
          }
        }

        this.position();
      }

      onPointerLeave() {
        this.classList.remove('is-dismissed');
      }

      onDocumentClick(event) {
        if (!this.isOpen) return;
        if (this.contains(event.target)) return;
        this.close();
      }

      onKeydown(event) {
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        var focusInside = this.contains(document.activeElement);
        this.close();
        /* Also kills a bubble put on screen by CSS :hover or by focus. */
        this.classList.add('is-dismissed');
        if (focusInside && this.button) this.button.focus();
      }

      onFocusOut(event) {
        var next = event.relatedTarget;
        if (next && this.contains(next)) return;
        this.close();
      }

      onResize() {
        if (this.isOpen || this.isVisible) this.position();
      }

      onBlockSelect(event) {
        if (!event.target || !event.target.contains(this)) return;
        this.open();
      }

      onBlockDeselect(event) {
        if (!event.target || !event.target.contains(this)) return;
        this.close();
      }

      open() {
        this.classList.remove('is-dismissed');
        this.classList.add('is-open');
        if (this.button) this.button.setAttribute('aria-expanded', 'true');
        this.position();
      }

      close() {
        this.classList.remove('is-open');
        if (this.button) this.button.setAttribute('aria-expanded', 'false');
      }

      position() {
        if (!this.bubble) return;

        this.style.removeProperty('--selvaro-tip-shift');
        this.style.removeProperty('--selvaro-tip-arrow-shift');
        this.classList.remove('is-below');

        /* Measuring needs a laid-out box; force one while the bubble is still
           hidden (positioning ahead of the CSS hover reveal). */
        var forced = false;
        if (!this.isVisible) {
          this.bubble.style.display = 'block';
          forced = true;
        }

        var margin = 12;
        var viewportWidth = document.documentElement.clientWidth;
        var rect = this.bubble.getBoundingClientRect();
        var shift = 0;

        if (rect.left < margin) {
          shift = margin - rect.left;
        } else if (rect.right > viewportWidth - margin) {
          shift = viewportWidth - margin - rect.right;
        }

        if (shift !== 0) {
          this.style.setProperty('--selvaro-tip-shift', Math.round(shift) + 'px');
        }

        var shifted = this.bubble.getBoundingClientRect();

        /* Keep the arrow base inside the rounded corners of the bubble (12px
           radius + 8px arrow half-width), otherwise it looks detached when the
           bubble is clamped to the viewport edge. */
        var wrapper = this.getBoundingClientRect();
        var center = wrapper.left + wrapper.width / 2;
        var inset = 20;
        var minCenter = shifted.left + inset;
        var maxCenter = shifted.right - inset;
        var arrowShift = 0;

        if (maxCenter > minCenter) {
          if (center < minCenter) {
            arrowShift = minCenter - center;
          } else if (center > maxCenter) {
            arrowShift = maxCenter - center;
          }
        }

        if (arrowShift !== 0) {
          this.style.setProperty('--selvaro-tip-arrow-shift', Math.round(arrowShift) + 'px');
        }

        if (shifted.top < margin) {
          this.classList.add('is-below');
        }

        if (forced) {
          this.bubble.style.display = '';
        }
      }
    }
  );
}
